/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { TABULAR_VIEWS, consoleCommands, events } from 'modules/common/ui-controller';
import { createVisualCollapseStep } from 'modules/common/ui-controller/utils/shared-command-steps';
import { REPORT_TYPE } from 'modules/dashboard/constants';
import { paceActions } from '../slice';

// time the pace visuals stay hidden before the visual collapse command value is applied
const VISUAL_COLLAPSE_DELAY = 2000;
// actions to show or hide the visuals of each pace view
const VISUAL_DISPLAY_ACTIONS = {
  [REPORT_TYPE.PACE_TIME_WIDGET]: {
    [TABULAR_VIEWS.TOTAL_REVENUE]: paceActions.setRevenueIsDisplayPT,
    [TABULAR_VIEWS.TOTAL_OCCUPIED_ROOMS]: paceActions.setOccupiedRoomIsDisplayPT,
    [TABULAR_VIEWS.ADR]: paceActions.setAdrIsDisplayPT,
    [TABULAR_VIEWS.CANCELLATIONS]: paceActions.setCxlIsDisplayPT,
    [TABULAR_VIEWS.CANCELLATIONS_COUNT]: paceActions.setCxlCountIsDisplay,
  },
  [REPORT_TYPE.PACE_SEGMENT_WIDGET]: {
    [TABULAR_VIEWS.TOTAL_REVENUE]: paceActions.setRevenueIsDisplayPS,
    [TABULAR_VIEWS.TOTAL_OCCUPIED_ROOMS]: paceActions.setOccupiedRoomIsDisplayPS,
    [TABULAR_VIEWS.ADR]: paceActions.setAdrIsDisplayPS,
    [TABULAR_VIEWS.CANCELLATIONS]: paceActions.setCxlIsDisplayPS,
    [TABULAR_VIEWS.CANCELLATIONS_COUNT]: paceActions.setCxlCountIsDisplay,
  },
  [REPORT_TYPE.PACE_HOTEL_WIDGET]: {
    [TABULAR_VIEWS.TOTAL_REVENUE]: paceActions.setRevenueIsDisplayPH,
    [TABULAR_VIEWS.TOTAL_OCCUPIED_ROOMS]: paceActions.setOccupiedRoomIsDisplayPH,
    [TABULAR_VIEWS.ADR]: paceActions.setAdrIsDisplayPH,
    [TABULAR_VIEWS.CANCELLATIONS]: paceActions.setCxlIsDisplayPH,
    [TABULAR_VIEWS.CANCELLATIONS_COUNT]: paceActions.setCxlCountIsDisplay,
  },
};
// active view command is in the url and the view is not opened yet
const isActiveViewPending = (context, { pending }) =>
  Object.hasOwn(pending, consoleCommands.ACTIVE_TREND_TAB) &&
  String(pending[consoleCommands.ACTIVE_TREND_TAB]) !== context.activeTab;
/**
 * Pace tab visual collapse commands, applied after the filter, pms sync and vi response commands.
 * context needs activeTab.
 */
const PACE_COLLAPSE_COMMAND_STEPS = [
  // active view command is read by the visual collapse step
  {
    command: consoleCommands.ACTIVE_TREND_TAB,
    event: events.SET_PACE_ACTIVE_TAB,
    listenOnly: true,
  },
  createVisualCollapseStep(
    events.SET_PACE_VISUAL_COLLAPSE,
    VISUAL_COLLAPSE_DELAY,
    (context) => VISUAL_DISPLAY_ACTIONS[context.activeTab],
    {
      requires: [consoleCommands.ACTIVE_TREND_TAB],
      // visuals of the view in the url are collapsed, wait until that view is opened
      ready: (context, run) => !isActiveViewPending(context, run),
    }
  ),
];

export default PACE_COLLAPSE_COMMAND_STEPS;
